import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroImage from '../../assets/images/Hero.webp';
import CardSwap, { Card } from '../../components/common/CardSwap';
import emailjs from '@emailjs/browser';
import SEO from '../../components/common/SEO';

function Home() {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});
  const [typedText, setTypedText] = useState({ part1: '', part2: '', done: false });

  useEffect(() => {
    const p1 = "Power Your Solutions with ";
    const p2 = "Clean Solar Energy";
    let i = 0;
    let phase = 1;
    let timeoutId;

    function type() {
      if (phase === 1) {
        if (i <= p1.length) {
          setTypedText({ part1: p1.slice(0, i), part2: '', done: false });
          i++;
          timeoutId = setTimeout(type, i === p1.length ? 80 : 55);
        } else {
          phase = 2;
          i = 0;
          timeoutId = setTimeout(type, 100);
        }
      } else {
        if (i <= p2.length) {
          setTypedText({ part1: p1, part2: p2.slice(0, i), done: i === p2.length });
          i++;
          timeoutId = setTimeout(type, i === p2.length ? 0 : 65);
        }
      }
    }

    timeoutId = setTimeout(type, 400);
    return () => clearTimeout(timeoutId);
  }, []);

  const [formData, setFormData] = useState({
    from_name: '',
    phone: '',
    from_email: '',
    sector: '',
    size: '',
    message: '',
  });

  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^(0\d{10}|[1-9]\d{9})$/;

    if (!emailRegex.test(formData.from_email) || !phoneRegex.test(formData.phone)) {
      setStatus('validation_error');
      return;
    }

    setSending(true);
    setStatus(null);
    try {
      await emailjs.send(
        'service_j7njzim',
        'template_2hnw1f3',
        formData,
        '0t3myGNVWRrrx-J7U'
      );
      setStatus('success');
      setFormData({ from_name: '', phone: '', from_email: '', sector: '', size: '', message: '' });
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach(key => {
      const element = sectionRefs.current[key];
      if (!element) return;
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setIsVisible(prev => ({ ...prev, [key]: true }));
        },
        { threshold: 0.2 }
      );
      observers[key].observe(element);
    });
    return () => Object.values(observers).forEach(observer => observer.disconnect());
  }, []);

  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    function onResize() { setScreenWidth(window.innerWidth); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="bg-white">
      <SEO title="Home" url="/" />

      {/* hero section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HeroImage} alt="Solar Panels" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-900/70 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent animate-shine"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              {typedText.part1}
              <span className="text-yellow-400">{typedText.part2}</span>
              {!typedText.done && (
                <span
                  className="inline-block align-middle ml-1 bg-yellow-400"
                  style={{ width: '3px', height: '0.85em', animation: 'cursorBlink 0.7s step-end infinite' }}
                />
              )}
            </h1>
            <p
              className="text-lg sm:text-xl md:text-2xl text-blue-100 mb-10"
              style={{ opacity: typedText.done ? 1 : 0, transition: 'opacity 0.8s ease' }}
            >
              Leading EPC solar company delivering sustainable energy solutions across Pakistan
            </p>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 py-4 sm:px-10 sm:py-5 bg-yellow-400 text-blue-900 font-bold text-base sm:text-lg rounded-lg hover:bg-yellow-300 transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-yellow-400/50"
              style={{ opacity: typedText.done ? 1 : 0, transition: 'opacity 0.8s ease' }}
            >
              Explore Our Services
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>

        <style>{`
          @keyframes cursorBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        `}</style>
      </section>

      {/* about section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div
            ref={el => sectionRefs.current['header'] = el}
            className={`text-center mb-16 transition-all duration-1000 ${isVisible['header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Who We Are</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-yellow-400 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">

            <div
              ref={el => sectionRefs.current['story'] = el}
              className={`group relative transition-all duration-1000 delay-200 ${isVisible['story'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-500"></div>
              <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-2xl transform group-hover:-translate-y-2 transition-all duration-500">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Story</h3>
                </div>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p className="text-base sm:text-lg">
                    <span className="text-blue-600 font-bold text-xl sm:text-2xl">Backed by over 70 years</span> of Industrial Experience, we entered the renewable industry in 2019.
                  </p>
                  <p>Beginning with commercial projects, we quickly realized we could make a bigger impact helping our employees, companies, communities and country on industrial and utility projects.</p>
                  <p>The renewable industry was in its infancy and we threw ourselves into it with full force. Since then we have built upon{' '}
                    <span className="font-semibold text-blue-600">Fauji Foundation legacy</span>, leading the way in innovation and bringing better value and experience on all our projects.
                  </p>
                </div>
                <div className="absolute inset-0 rounded-2xl border-2 border-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
              </div>
            </div>

            <div className="space-y-8">
              <div
                ref={el => sectionRefs.current['about'] = el}
                className={`group relative transition-all duration-1000 delay-400 ${isVisible['about'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-2xl transform -rotate-2 group-hover:-rotate-3 transition-transform duration-500"></div>
                <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-2xl transform group-hover:-translate-y-2 transition-all duration-500">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">About Us</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                    <span className="font-bold text-yellow-600">Foundation Solar Energy PVT Limited</span> is part of the Fauji Foundation FSJ Group. EF Group has decades of experience in "Energy," Foods, "Off and "Gas, "Cement, Fertilizer and Healthcare Sectors.
                  </p>
                  <div className="absolute inset-0 rounded-2xl border-2 border-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                </div>
              </div>

              <div
                ref={el => sectionRefs.current['services'] = el}
                className={`group relative transition-all duration-1000 delay-600 ${isVisible['services'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-green-700 rounded-2xl transform -rotate-1 group-hover:-rotate-2 transition-transform duration-500"></div>
                <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-2xl transform group-hover:-translate-y-2 transition-all duration-500">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Services</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-base sm:text-lg mb-6">
                    We provide <span className="font-semibold text-green-600">cost-effective Commercial, Agricultural and Industrial Solar Solutions</span> using innovative technologies along with necessary Operational and Maintenance Services.
                  </p>
                  <Link to="/services" className="inline-flex items-center gap-2 text-green-600 font-semibold hover:gap-3 transition-all group">
                    Explore Our Services
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <div className="absolute inset-0 rounded-2xl border-2 border-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* cardswap section */}
<section className="pt-16 pb-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 relative overflow-hidden">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">

      <div className="max-w-xl text-center lg:text-left w-full">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 lg:mb-6">
          Our Operational Excellence
        </h2>
        <p className="text-base sm:text-lg lg:text-xl font-bold text-blue-100 mb-6 lg:mb-8 leading-relaxed">
          We ensure peak performance and reliability through continuous monitoring, innovative optimization, and dedicated maintenance of every solar installation.
        </p>
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-500 text-white font-bold text-sm sm:text-base rounded-lg hover:bg-blue-400 transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/50"
        >
          Look at our Portfolio
          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>

      <div className="w-full flex items-center justify-center lg:justify-end">
        {screenWidth < 1024 ? (
          <div
            className="relative"
            style={{
              width: '100%',
              maxWidth: screenWidth < 640 ? '400px' : screenWidth < 768 ? '450px' : '550px',
              height: screenWidth < 640 ? '500px' : screenWidth < 768 ? '550px' : '600px',
            }}
          >
            <CardSwap
              width={screenWidth < 640 ? 380 : screenWidth < 768 ? 420 : 520}
              height={screenWidth < 640 ? 300 : screenWidth < 768 ? 320 : 350}
              cardDistance={screenWidth < 640 ? 35 : screenWidth < 1024 ? 45 : 50}
              verticalDistance={screenWidth < 640 ? 45 : screenWidth < 1024 ? 60 : 60}
              delay={4000}
              pauseOnHover={true}
              easing="elastic"
            >
              <Card customClass="overflow-hidden shadow-2xl">
                <div className="absolute inset-0">
                  <img src={require('../../assets/images/img1.png')} alt="Product Innovation" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-white/40"></div>
                </div>
                <div className="relative z-10 h-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center">
                  <h3 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">Product Innovation</h3>
                  <p className="text-xs sm:text-sm lg:text-lg text-gray-700 leading-relaxed font-semibold">Our Innovation Lab optimizes solar and storage systems by analyzing design, inverters, and cleaning frequency.</p>
                </div>
              </Card>
              <Card customClass="overflow-hidden shadow-2xl">
                <div className="absolute inset-0">
                  <img src={require('../../assets/images/img2.png')} alt="Network Operations Center" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-white/40"></div>
                </div>
                <div className="relative z-10 h-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center">
                  <h3 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">Network Operations Center (NOC)</h3>
                  <p className="text-xs sm:text-sm lg:text-lg text-gray-700 leading-relaxed font-semibold">A 24/7 Network Operations Center remotely monitors and controls performance.</p>
                </div>
              </Card>
              <Card customClass="overflow-hidden shadow-2xl">
                <div className="absolute inset-0">
                  <img src={require('../../assets/images/img3.png')} alt="Maintenance Strategy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-white/40"></div>
                </div>
                <div className="relative z-10 h-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center">
                  <h3 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">Maintenance Strategy</h3>
                  <p className="text-xs sm:text-sm lg:text-lg text-gray-700 leading-relaxed font-semibold">I-V curve tracing and thermal imaging offer insights to optimize efficiency.</p>
                </div>
              </Card>
              <Card customClass="overflow-hidden shadow-2xl">
                <div className="absolute inset-0">
                  <img src={require('../../assets/images/img4.png')} alt="On-Site Team" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-white/40"></div>
                </div>
                <div className="relative z-10 h-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center">
                  <h3 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">On-Site Team</h3>
                  <p className="text-xs sm:text-sm lg:text-lg text-gray-700 leading-relaxed font-semibold">An on-ground O&M team conducts regular site surveys, maintenance, and proactive or corrective actions.</p>
                </div>
              </Card>
            </CardSwap>
          </div>
        ) : (
          <div className="relative" style={{ width: '100%', maxWidth: '600px', height: '600px' }}>
            <div style={{ position: 'absolute', inset: 0, transform: 'translateX(-60px)' }}>
              <CardSwap width={600} height={320} cardDistance={50} verticalDistance={60} delay={4000} pauseOnHover={true} easing="elastic">
                <Card customClass="overflow-hidden shadow-2xl">
                  <div className="absolute inset-0">
                    <img src={require('../../assets/images/img1.png')} alt="Product Innovation" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-white/40"></div>
                  </div>
                  <div className="relative z-10 h-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center">
                    <h3 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">Product Innovation</h3>
                    <p className="text-xs sm:text-sm lg:text-lg text-gray-700 leading-relaxed font-semibold">Our Innovation Lab optimizes solar and storage systems by analyzing design, inverters, and cleaning frequency.</p>
                  </div>
                </Card>
                <Card customClass="overflow-hidden shadow-2xl">
                  <div className="absolute inset-0">
                    <img src={require('../../assets/images/img2.png')} alt="Network Operations Center" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-white/40"></div>
                  </div>
                  <div className="relative z-10 h-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center">
                    <h3 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">Network Operations Center (NOC)</h3>
                    <p className="text-xs sm:text-sm lg:text-lg text-gray-700 leading-relaxed font-semibold">A 24/7 Network Operations Center remotely monitors and controls performance.</p>
                  </div>
                </Card>
                <Card customClass="overflow-hidden shadow-2xl">
                  <div className="absolute inset-0">
                    <img src={require('../../assets/images/img3.png')} alt="Maintenance Strategy" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-white/40"></div>
                  </div>
                  <div className="relative z-10 h-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center">
                    <h3 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">Maintenance Strategy</h3>
                    <p className="text-xs sm:text-sm lg:text-lg text-gray-700 leading-relaxed font-semibold">I-V curve tracing and thermal imaging offer insights to optimize efficiency.</p>
                  </div>
                </Card>
                <Card customClass="overflow-hidden shadow-2xl">
                  <div className="absolute inset-0">
                    <img src={require('../../assets/images/img4.png')} alt="On-Site Team" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-white/40"></div>
                  </div>
                  <div className="relative z-10 h-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center">
                    <h3 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">On-Site Team</h3>
                    <p className="text-xs sm:text-sm lg:text-lg text-gray-700 leading-relaxed font-semibold">An on-ground O&M team conducts regular site surveys, maintenance, and proactive or corrective actions.</p>
                  </div>
                </Card>
              </CardSwap>
            </div>
          </div>
        )}
      </div>

    </div>
  </div>
</section>

      {/* free quotation section */}
      <section className="py-16 sm:py-24 bg-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            <div>
              <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Don't Know What To{' '}
                <span className="text-blue-700">Start With?</span>
                <br />
                <span className="text-2xl sm:text-4xl font-semibold text-gray-700">
                  Get a Solution For All Your{' '}
                  <span className="text-yellow-500">Solar Needs</span>
                </span>
              </h2>
              <p className="text-gray-500 text-base sm:text-lg leading-relaxed">
                Fill in your details and our team will get back to you with a customized solar solution — completely free of charge.
              </p>
              <div className="mt-8 w-20 h-1 bg-gradient-to-r from-blue-600 to-yellow-400 rounded-full"></div>
            </div>

            <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-6 sm:p-10 shadow-xl border border-blue-800">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Full Name</label>
                  <input type="text" name="from_name" value={formData.from_name} onChange={handleChange} placeholder="Your Name"
                    className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+92 300 0000000"
                    className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Email Address</label>
                  <input type="email" name="from_email" value={formData.from_email} onChange={handleChange} placeholder="youremail@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Sector</label>
                  <select name="sector" value={formData.sector} onChange={handleChange} required
                    className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none">
                    <option value="" disabled>Select Sector</option>
                    <option value="Residential">Residential</option>
                    <option value="Industrial/Commercial">Industrial/Commercial</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">System Size</label>
                  <select name="size" value={formData.size} onChange={handleChange} required
                    className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none">
                    <option value="" disabled>Select Size</option>
                    <option value="5kw-25kw">5kw - 25kw</option>
                    <option value="25kw-100kw">25kw - 100kw</option>
                    <option value="100kw-1MW">100kw - 1MW</option>
                    <option value="1MW-10MW">1MW - 10MW</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-200 mb-1">Service Description</label>
                  <textarea rows={4} name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your energy needs, project size, location..."
                    className="w-full px-4 py-3 rounded-xl border border-blue-700 bg-slate-800 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none" />
                </div>
                {status && (
                  <p className={`text-sm font-semibold text-center ${status === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                    {status === 'success'
                      ? '✓ Your quote request has been sent! We will get back to you soon.'
                      : status === 'validation_error'
                      ? '✗ pls enter correct format'
                      : '✗ Something went wrong. Please try again.'}
                  </p>
                )}
                <button type="button" onClick={handleSubmit} disabled={sending}
                  className="w-full py-4 bg-gradient-to-r from-blue-700 to-blue-900 text-white font-bold text-lg rounded-xl hover:from-blue-600 hover:to-blue-800 transform hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-blue-500/40 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100">
                  {sending ? 'Sending...' : 'Get your Quote'}
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}

export default Home;